import type { FC, ReactNode } from 'react';
import { createContext, useCallback, useEffect, useState } from 'react';
import { companySettingsApi, setCompanyStore } from '../api/supabase/company-settings';
import type { CompanySettings, CompanySettingsUpdate } from '../api/supabase/company-settings';

export interface CompanySettingsContextType {
  settings: CompanySettings;
  isLoaded: boolean;
  isLoading: boolean;
  /** Reload company settings from Supabase */
  reload: () => Promise<void>;
  /** Update company settings */
  update: (updates: CompanySettingsUpdate) => Promise<CompanySettings>;
}

const DEFAULT_SETTINGS: CompanySettings = {
  id: '',
  companyName: 'Mystic Media',
  fullCompanyName: 'Mystic Media Film',
  tagline: 'Video & Media Production',
  logoUrl: null,
  logoDarkUrl: null,
  faviconUrl: null,
  primaryColor: '#2970FF',
  website: 'https://mysticmediafilm.com',
  phone: '(813) 815-0142',
  email: 'sales@mysticmediafilm.com',
  address: 'Katy, TX',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

export const CompanySettingsContext = createContext<CompanySettingsContextType>({
  settings: DEFAULT_SETTINGS,
  isLoaded: false,
  isLoading: false,
  reload: async () => {},
  update: async () => DEFAULT_SETTINGS,
});

interface CompanySettingsProviderProps {
  children?: ReactNode;
}

export const CompanySettingsProvider: FC<CompanySettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<CompanySettings>(DEFAULT_SETTINGS);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await companySettingsApi.get();
      setCompanyStore(data);
      setSettings(data);
    } catch (err: any) {
      console.warn('Failed to load company settings:', err.message);
    }
    setIsLoaded(true);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const update = useCallback(async (updates: CompanySettingsUpdate): Promise<CompanySettings> => {
    const updated = await companySettingsApi.update(updates);
    setCompanyStore(updated);
    setSettings(updated);
    return updated;
  }, []);

  return (
    <CompanySettingsContext.Provider
      value={{ settings, isLoaded, isLoading, reload: loadSettings, update }}
    >
      {children}
    </CompanySettingsContext.Provider>
  );
};
